import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import InterviewColumn from './InterviewColumn';
import { Interview, InterviewStage, InterviewStageName } from '../../types/interview';
import { interviewApi } from '../../services/api/interview';
import { ApplicationStatus } from '../../types/application';
import { FiLoader } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';

interface InterviewBoardProps {
  applicationId: number;
  jobId: number;
  onStatusChange?: (applicationId: number, newStatus: ApplicationStatus) => void;
  onOpenNotes: (interview: Interview, stage: InterviewStage) => void;
}

// Move this to a shared constants file if used elsewhere
export const INTERVIEW_STAGES: InterviewStageName[] = [
  'screening',
  'technical',
  'behavioral',
  'final',
  'offer',
  'completed'
];

const InterviewBoard: React.FC<InterviewBoardProps> = ({
  applicationId,
  jobId,
  onStatusChange,
  onOpenNotes
}) => {
  const [stages, setStages] = useState<InterviewStage[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (applicationId && user?.id) {
      loadStages();
    }
  }, [applicationId, user?.id]);

  const loadStages = async () => {
    try {
      setLoading(true);
      const data = await interviewApi.getInterviews(applicationId, jobId);
      setStages(data);
    } catch (error) {
      console.error('Failed to load stages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (stageId: number, newStage: InterviewStageName) => {
    try {
      console.log(`Moving stage ${stageId} to ${newStage}`); // Debug log
      await interviewApi.updateInterviewStage(stageId, {
        name: newStage,
        order: INTERVIEW_STAGES.indexOf(newStage)
      });

      // Update application status based on stage
      if (onStatusChange) {
          await onStatusChange(applicationId, ApplicationStatus.INTERVIEWING);
      }

      // Always reload stages after any stage change
      await loadStages();
    } catch (error) {
      console.error('Failed to update stage:', error);
    }
  };

  const handleStageComplete = async (stageId: number) => {
    try {
      await interviewApi.updateInterviewStage(stageId, {
        name: 'completed',
        order: 999
      });
      
      if (onStatusChange) {
        await onStatusChange(applicationId, ApplicationStatus.ACCEPTED);
      }
      
      await loadStages();
    } catch (error) {
      console.error('Failed to complete stage:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <FiLoader className="w-8 h-8 text-purple-500 animate-spin" />
      </div>
    );
  }

  // Group stages by name and ensure all stages exist, including offer stage
  const stageColumns = INTERVIEW_STAGES.map(stageName => {
    const existingStage = stages.find(s => s.name === stageName);
    const stage = existingStage || {
      id: -1,
      name: stageName,
      order: INTERVIEW_STAGES.indexOf(stageName),
      user_id: user?.id || '',
      job_application_id: applicationId,
      job_id: jobId,
      interview: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    // Ensure interview data is preserved when moving to offer stage
    const interview = stage.interview ? {
      ...stage.interview,
      job_id: stage.job_id,
      stage: stageName // Ensure stage name is updated
    } : null;
    
    return {
      stageName,
      stage,
      interview
    };
  });

  return (
    <div className="mt-6">
      <motion.div 
        className="flex gap-4 overflow-x-auto pb-6 snap-x"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <AnimatePresence mode="popLayout">
          {stageColumns.map(({ stageName, stage, interview }, index) => (
            <motion.div
              key={stageName}
              className="snap-start"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <InterviewColumn
                stageName={stageName}
                stage={stage}
                interview={interview}
                onStageChange={handleStageChange}
                onStageComplete={handleStageComplete}
                onOpenNotes={onOpenNotes}
                index={index}
                allStages={INTERVIEW_STAGES}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default InterviewBoard; 